/* eslint-disable react/jsx-no-bind */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import * as colors from '@constants/colors';
import { withStyles } from '@material-ui/core/styles';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import { Checkbox } from '@mui';
import Cell from '../cell';
import Actions from './actions';
import Value from './value';

const CustomRow = withStyles(theme => ({  // eslint-disable-line no-unused-vars
  root: {
    backgroundColor: colors.overlayTextColor,
    cursor: 'pointer'
  }
}))(TableRow);

const getColumnWidth = width => width ? ({
  maxWidth: `${width}rem`,
  minWidth: `${width}rem`,
  width: `${width}rem`
}) : {};

const Row = ({ row, index, actions, actionsRenderer, checked, columns, onRowClick, onToggle, stickyFirstColumn }) => {
  const [hovered, setHovered] = useState(false);
  const hoverOn = () => setHovered(true);
  const hoverOff = () => setHovered(false);
  const onChange = useCallback(() => onToggle(index), [index, onToggle]);

  // Determine if we should show actions (either custom renderer or built-in actions)
  const hasActions = actionsRenderer || (actions && actions.length > 0);

  return (
    <CustomRow
      hover
      key={`body-row-${row.id}`}
      tabIndex={0}
      onClick={event => onRowClick(event, row, index)}
      onKeyDown={event => {
        if (event.key === 'Enter') {
          onRowClick(event, row, index);
        }
      }}
      onMouseEnter={hoverOn}
      onMouseLeave={hoverOff}
    >
      {columns.map((column, columnIndex) => {
        const isFirstColumn = columnIndex === 0 && stickyFirstColumn;
        const style = {
          ...getColumnWidth(column.width),
          ...(column.style || {}),
          boxSizing: 'border-box',
          ...(isFirstColumn && {
            position: 'sticky',
            left: 0,
            zIndex: 1,
            backgroundColor: hovered ? colors.dotmapsLightGray : colors.overlayTextColor
          })
        };
        if (column.key === 'checkbox') {
          return (
            <Cell key={`body-cell-${column.key}`} style={style}>
              <Checkbox
                checked={checked[index] === true}
                onChange={onChange}
                onClick={event => event.stopPropagation()}
                size="small"
              />
            </Cell>
          );
        }
        if (hovered && columnIndex === columns.length - 1 && hasActions) {
          return (
            <Cell key={`body-cell-${column.key}`} style={style}>
              {actionsRenderer ? actionsRenderer(row) : <Actions actions={actions} row={row} />}
            </Cell>
          );
        }
        return (
          <Cell key={`body-cell-${column.key}`} style={style}>
            <Value column={column} row={row} />
          </Cell>
        );
      })}
    </CustomRow>
  );
};

Row.propTypes = {
  actions: PropTypes.array,
  actionsRenderer: PropTypes.func,
  checked: PropTypes.array,
  columns: PropTypes.array,
  index: PropTypes.number,
  onRowClick: PropTypes.func,
  onToggle: PropTypes.func,
  row: PropTypes.object,
  stickyFirstColumn: PropTypes.bool
};

const Body = ({ actions, actionsRenderer, checked, columns, onRowClick, onToggle, rows, stickyFirstColumn }) => (
  <TableBody>
    {rows.map((row, index) => (
      <Row
        actions={actions}
        actionsRenderer={actionsRenderer}
        checked={checked}
        columns={columns}
        index={index}
        key={`body-row-wrapper-${row.id}`}
        onToggle={onToggle}
        onRowClick={onRowClick}
        row={row}
        stickyFirstColumn={stickyFirstColumn}
      />
    ))}
  </TableBody>
);

Body.propTypes = {
  actions: PropTypes.array,
  actionsRenderer: PropTypes.func,
  checked: PropTypes.array,
  columns: PropTypes.array,
  onRowClick: PropTypes.func,
  onToggle: PropTypes.func,
  rows: PropTypes.array,
  stickyFirstColumn: PropTypes.bool
};

export default Body;
